/**
 * Construtor da cadeia de efeitos de uma faixa.
 *
 * IMPORTANTE: a mesma função é usada em tempo real (AudioContext) e na
 * exportação (OfflineAudioContext). Assim o que o usuário ouve é
 * exatamente o que sai no arquivo — sem "mix fantasma".
 */
import type { EqBand, FxConfig } from '../types.js';
import { dbToGain, clamp } from '../utils/misc.js';
import { REVERB_SPECS } from './fx.js';

export interface CtxLike {
  sampleRate: number;
  currentTime: number;
  destination: AudioNode;
  createGain(): GainNode;
  createBiquadFilter(): BiquadFilterNode;
  createDelay(max?: number): DelayNode;
  createDynamicsCompressor(): DynamicsCompressorNode;
  createConvolver(): ConvolverNode;
  createStereoPanner(): StereoPannerNode;
  createChannelSplitter(channels?: number): ChannelSplitterNode;
  createChannelMerger(channels?: number): ChannelMergerNode;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBuffer;
  createBufferSource(): AudioBufferSourceNode;
  createAnalyser?(): AnalyserNode;
  audioWorklet?: { addModule(url: string): Promise<void> };
}

export interface FxRefs {
  input: GainNode;
  output: GainNode;
  trim: GainNode;
  highPass: BiquadFilterNode;
  gateNode: AudioNode | null;
  gateWorklet: AudioWorkletNode | null;
  eqNodes: BiquadFilterNode[];
  deEsserNode: AudioWorkletNode | null;
  deEsserFallback: BiquadFilterNode;
  comp: DynamicsCompressorNode;
  compEnabledGain: GainNode;
  compBypassGain: GainNode;
  presence: BiquadFilterNode;
  detuneNode: AudioWorkletNode | null;
  doubleDelay: DelayNode | null;
  doubleGain: GainNode | null;
  doublePan: StereoPannerNode | null;
  dry: GainNode;
  reverbSend: GainNode;
  delaySend: GainNode;
  pan: StereoPannerNode;
}

export interface BuildOptions {
  ctx: CtxLike;
  /** Nós de envio compartilhados (reverb/delay) — podem ser null no offline. */
  sends?: { reverbIn: AudioNode; delayIn: AudioNode } | null;
  /** Worklets já carregados: nome -> true. */
  worklets?: Record<string, boolean>;
  withAnalyser?: boolean;
}

export function createEqNodes(ctx: CtxLike, bands: EqBand[]): BiquadFilterNode[] {
  return bands.map((b) => {
    const f = ctx.createBiquadFilter();
    f.type = b.type;
    f.frequency.value = b.freqHz;
    f.Q.value = b.q;
    f.gain.value = b.gainDb;
    return f;
  });
}

/**
 * De-esser multibanda por envelope (AudioWorklet). Se Worklet não existir,
 * usa um shelf fixo bem suave como fallback — nunca um botão sem ação.
 */
function makeDeEsser(ctx: CtxLike, worklets?: Record<string, boolean>) {
  let node: AudioWorkletNode | null = null;
  if (worklets?.['vocal-deesser']) {
    try {
      node = new AudioWorkletNode(ctx as unknown as BaseAudioContext, 'vocal-deesser', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
    } catch {
      node = null;
    }
  }
  const fallback = ctx.createBiquadFilter();
  fallback.type = 'highshelf';
  fallback.frequency.value = 6800;
  fallback.gain.value = 0;
  return { node, fallback };
}

/** Monta a cadeia completa de uma faixa. */
export function buildFxChain(opts: BuildOptions): { refs: FxRefs; input: AudioNode; output: AudioNode } {
  const { ctx, sends, worklets } = opts;
  const input = ctx.createGain();
  const output = ctx.createGain();

  const trim = ctx.createGain();
  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = 90;
  highPass.Q.value = 0.707;

  // ---- Gate (AudioWorklet com lookahead; fallback = bypass honesto) ----
  let gateNode: AudioNode | null = null;
  let gateWorklet: AudioWorkletNode | null = null;
  if (worklets?.['vocal-gate']) {
    try {
      gateWorklet = new AudioWorkletNode(ctx as unknown as BaseAudioContext, 'vocal-gate', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      gateNode = gateWorklet;
    } catch {
      gateWorklet = null;
      gateNode = null;
    }
  }

  // ---- EQ ----
  const eqNodes = createEqNodes(ctx, [
    { freqHz: 80, gainDb: 0, q: 1, type: 'lowshelf' },
    { freqHz: 200, gainDb: 0, q: 1.1, type: 'peaking' },
    { freqHz: 500, gainDb: 0, q: 1.1, type: 'peaking' },
    { freqHz: 1000, gainDb: 0, q: 1.1, type: 'peaking' },
    { freqHz: 3000, gainDb: 0, q: 1.1, type: 'peaking' },
    { freqHz: 8000, gainDb: 0, q: 1.1, type: 'peaking' },
    { freqHz: 12000, gainDb: 0, q: 0.9, type: 'highshelf' },
  ]);

  // ---- De-esser ----
  const de = makeDeEsser(ctx, worklets);

  // ---- Compressor (com bypass cruzado para ligar/desligar sem clique) ----
  const comp = ctx.createDynamicsCompressor();
  const compEnabledGain = ctx.createGain();
  const compBypassGain = ctx.createGain();

  // ---- Presença ----
  const presence = ctx.createBiquadFilter();
  presence.type = 'highshelf';
  presence.frequency.value = 3200;
  presence.gain.value = 0;

  // ---- Double vocal (detune + micro delay + pan) ----
  let detuneNode: AudioWorkletNode | null = null;
  let doubleDelay: DelayNode | null = null;
  let doubleGain: GainNode | null = null;
  let doublePan: StereoPannerNode | null = null;
  if (worklets?.['vocal-detune']) {
    try {
      detuneNode = new AudioWorkletNode(ctx as unknown as BaseAudioContext, 'vocal-detune', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      doubleDelay = ctx.createDelay(1.0);
      doubleGain = ctx.createGain();
      doublePan = ctx.createStereoPanner();
    } catch {
      detuneNode = null;
    }
  }

  const dry = ctx.createGain();
  const reverbSend = ctx.createGain();
  const delaySend = ctx.createGain();
  const pan = ctx.createStereoPanner();

  // ---------------- conexões ----------------
  input.connect(trim);
  trim.connect(highPass);
  let node: AudioNode = highPass;
  if (gateNode) {
    node.connect(gateNode);
    node = gateNode;
  }
  for (const eq of eqNodes) {
    node.connect(eq);
    node = eq;
  }

  // De-esser multibanda. Worklet é dinâmico; fallback é um shelf discreto.
  if (de.node) {
    node.connect(de.node);
    node = de.node;
  } else {
    node.connect(de.fallback);
    node = de.fallback;
  }

  // Compressor principal (bypass cruzado)
  const preComp = node;
  preComp.connect(compEnabledGain);
  compEnabledGain.connect(comp);
  preComp.connect(compBypassGain);
  const postComp = ctx.createGain();
  comp.connect(postComp);
  compBypassGain.connect(postComp);
  node = postComp;

  node.connect(presence);
  node = presence;

  // Double vocal (paralelo)
  if (detuneNode && doubleDelay && doubleGain && doublePan) {
    node.connect(detuneNode);
    detuneNode.connect(doubleDelay);
    doubleDelay.connect(doubleGain);
    doubleGain.connect(doublePan);
    doublePan.connect(dry);
  }

  node.connect(dry);
  dry.connect(pan);
  node.connect(reverbSend);
  node.connect(delaySend);
  if (sends?.reverbIn) reverbSend.connect(sends.reverbIn);
  if (sends?.delayIn) delaySend.connect(sends.delayIn);
  pan.connect(output);

  return {
    refs: {
      input,
      output,
      trim,
      highPass,
      gateNode,
      gateWorklet,
      eqNodes,
      deEsserNode: de.node,
      deEsserFallback: de.fallback,
      comp,
      compEnabledGain,
      compBypassGain,
      presence,
      detuneNode,
      doubleDelay,
      doubleGain,
      doublePan,
      dry,
      reverbSend,
      delaySend,
      pan,
    },
    input,
    output,
  };
}

/** Aplica uma configuração de efeitos numa cadeia já construída. */
export function configureFx(refs: FxRefs, fx: FxConfig, now: number): void {
  const t = now;
  const set = (param: AudioParam, value: number, timeConstant = 0.02) => {
    try {
      param.cancelScheduledValues(t);
      param.setTargetAtTime(value, t, timeConstant);
    } catch {
      try {
        param.value = value;
      } catch {
        /* noop */
      }
    }
  };

  set(refs.trim.gain, dbToGain(fx.gainDb));
  set(refs.highPass.frequency, fx.highPassOn ? clamp(fx.highPassHz, 20, 400) : 20);
  refs.highPass.type = 'highpass';

  // Gate
  if (refs.gateWorklet) {
    refs.gateWorklet.port.postMessage({
      type: 'set',
      enabled: fx.gate.enabled,
      thresholdDb: fx.gate.thresholdDb,
      attackMs: fx.gate.attackMs,
      releaseMs: fx.gate.releaseMs,
      holdMs: fx.gate.holdMs,
      lookaheadMs: 12,
      kneeDb: 8,
    });
  }

  // EQ
  if (fx.eqMode === 'simple') {
    // 3 bandas largas mapeadas nas 7 bandas: low(80/200), mid(500/1k), high(3k/8k/12k)
    const map = [fx.lowGainDb, fx.lowGainDb * 0.6, fx.midGainDb, fx.midGainDb * 0.7, fx.highGainDb * 0.8, fx.highGainDb, fx.highGainDb * 0.9];
    refs.eqNodes.forEach((n, i) => set(n.gain, clamp(map[i] ?? 0, -18, 18)));
  } else {
    fx.eqBands.forEach((b, i) => {
      const n = refs.eqNodes[i];
      if (!n) return;
      n.type = b.type;
      set(n.frequency, clamp(b.freqHz, 20, 20000));
      set(n.Q, clamp(b.q, 0.1, 12));
      set(n.gain, clamp(b.gainDb, -18, 18));
    });
  }

  // De-esser dinâmico (Worklet) ou shelf suave no fallback.
  if (refs.deEsserNode) {
    refs.deEsserNode.port.postMessage({
      type: 'set',
      enabled: fx.deEsser.enabled,
      freqHz: clamp(fx.deEsser.freqHz, 3000, 12000),
      strength: fx.deEsser.strength,
    });
  } else {
    refs.deEsserFallback.frequency.setTargetAtTime(clamp(fx.deEsser.freqHz, 3000, 12000), t, 0.02);
    const fallbackDb = { suave: -1.5, medio: -2.5, forte: -3.5 }[fx.deEsser.strength];
    set(refs.deEsserFallback.gain, fx.deEsser.enabled ? fallbackDb : 0);
  }

  // Compressor
  const c = fx.comp;
  set(refs.comp.threshold, clamp(c.thresholdDb, -100, 0));
  set(refs.comp.ratio, clamp(c.ratio, 1, 20));
  set(refs.comp.attack, clamp(c.attackMs / 1000, 0.001, 1));
  set(refs.comp.release, clamp(c.releaseMs / 1000, 0.01, 5));
  set(refs.comp.knee, clamp(c.kneeDb, 0, 40));
  set(refs.compEnabledGain.gain, c.enabled ? dbToGain(c.makeupDb) : 0);
  set(refs.compBypassGain.gain, c.enabled ? 0 : 1);

  // Presença
  set(refs.presence.gain, clamp(fx.presenceDb, -12, 12));

  // Double vocal
  if (refs.detuneNode && refs.doubleDelay && refs.doubleGain && refs.doublePan) {
    refs.detuneNode.port.postMessage({
      type: 'set',
      enabled: fx.double.enabled,
      cents: fx.double.detuneCents,
      rateHz: 0.35,
      mix: fx.double.enabled ? 1 : 0,
    });
    set(refs.doubleDelay.delayTime, clamp(fx.double.delayMs / 1000, 0, 0.5));
    set(refs.doubleGain.gain, fx.double.enabled ? dbToGain(fx.double.levelDb) : 0);
    set(refs.doublePan.pan, clamp(fx.double.pan, -1, 1));
  }

  // Sends
  set(refs.reverbSend.gain, fx.reverb.enabled ? dbToGain(fx.reverb.sendDb) : 0);
  set(refs.delaySend.gain, fx.delay.enabled ? dbToGain(fx.delay.sendDb) : 0);
  set(refs.pan.pan, 0);
}

/** Configura o bus de delay compartilhado. */
export function configureDelayBus(
  nodes: { delay: DelayNode; feedback: GainNode; wet: GainNode },
  timeMs: number,
  feedback: number
) {
  nodes.delay.delayTime.value = clamp(timeMs / 1000, 0.01, 1.5);
  nodes.feedback.gain.value = clamp(feedback, 0, 0.9);
}

export const REVERB_LABEL = REVERB_SPECS;
